import { useFormikContext } from "formik";
import { array, object, string, InferType } from "yup";
import { Callout, Code, Flex } from "@radix-ui/themes";
import SelectField from "@/components/form/SelectField";
import FilterConditionsField from "./shared/FilterConditionsField";

export const conditionValidationSchema = object({
  parameters: object().shape({
    filter_conditions: array()
      .of(
        object({
          field: string().required("Field is required"),
          operator: string().required("Operator is required"),
          value: string().when("operator", {
            is: (op: string) => op !== "is_null" && op !== "is_not_null",
            then: (schema) => schema.required("Value is required"),
            otherwise: (schema) => schema.notRequired(),
          }),
        })
      )
      .min(1, "At least one filter condition is required"),
    logical_operator: string()
      .required("Logical operator is required")
      .oneOf(["AND", "OR"], "Logical operator must be AND or OR"),
  }),
});

export type ConditionFormValues = InferType<typeof conditionValidationSchema>;

export default function ConditionFields() {
  const { values } = useFormikContext<ConditionFormValues>();

  // Initialize filter conditions if they don't exist
  if (!values.parameters.filter_conditions) {
    values.parameters.filter_conditions = [];
  }

  return (
    <Flex direction="column" gap="4">
      <Callout.Root mb="4">
        <Callout.Text>
          Use <Code size="1">{`{{ }}`}</Code> syntax to reference dynamic values
          in filter conditions. For the <Code size="1">Field</Code> field, you
          can just reference the value directly without the curly braces, for
          example <Code size="1">$anythink.steps.readUsers.data[0].name</Code>.
        </Callout.Text>
      </Callout.Root>

      {/* Filter Conditions */}
      <FilterConditionsField
        name="parameters.filter_conditions"
        label="Filter Conditions"
        required
      />

      {/* Logical Operator */}
      <Flex direction="column" style={{ maxWidth: "300px" }}>
        <SelectField
          name="parameters.logical_operator"
          label="Logical Operator"
          required
          type="string"
        >
          <SelectField.Item value="AND">
            AND (all conditions must match)
          </SelectField.Item>
          <SelectField.Item value="OR">
            OR (any condition must match)
          </SelectField.Item>
        </SelectField>
      </Flex>
    </Flex>
  );
}
